import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils/formatting';
import { Stack } from 'components';

import MarketEnergyTariffsLabel from 'components/MarketEnergyTariffs/Label';
import { EnergyMarketTariffProvider } from 'components/MarketEnergyTariffs/Context';
import { Label } from 'semantic';

import { EvseControllerCostSettings } from 'types/evse-controller';
import { useAdvancedTariffFeatures } from 'components/tariffs/features';
import { EvseController } from 'types/evse-controller';

export default function LocationCostSettingCell({
  item,
  currency,
}: {
  item?: EvseController;
  currency?: string;
}) {
  const advancedTariffFeatures = useAdvancedTariffFeatures();
  const { t } = useTranslation();

  if (!item) {
    return '-';
  }

  if (
    advancedTariffFeatures.advancedTariffs &&
    item.costSettings?.some((costSetting) => costSetting.components?.length > 1)
  ) {
    return (
      <Label
        content={t(
          'evseControllerCostsLabel.advancedTariff',
          'Advanced Tariff'
        )}
      />
    );
  }

  if (item.enablePublicFreeCharging) {
    return (
      <Label
        content={t(
          'evseControllerCostsLabel.enablePublicFreeCharging',
          'Free for any RFID tag'
        )}
      />
    );
  }

  if (!item.enablePublicCharging) {
    return (
      <Label
        content={t(
          'evseControllerCostsLabel.enablePublicCharging',
          'Roaming disabled'
        )}
      />
    );
  }

  if (item.tariffProfileId && !item.enableTariffProfileOverride) {
    return (
      <Label
        content={t(
          'evseControllerCostsLabel.enableTariffProfileOverride',
          'Tariff Profile'
        )}
      />
    );
  }

  if (!item.costSettings?.length) {
    return null;
  }

  return (
    <>
      {item.costSettings[0] && (
        <CostSetting costSetting={item.costSettings[0]} currency={currency} />
      )}
    </>
  );
}

function CostSetting({
  costSetting,
  currency,
}: {
  currency?: string;
  costSetting: EvseControllerCostSettings | undefined;
}) {
  const { t } = useTranslation();
  if (!costSetting || !currency) {
    return 'N / A';
  }
  const pricePerKwh = costSetting.pricePerKwh;

  if (costSetting.enableEnergyMarketTracking) {
    return (
      <>
        {/*
        Area hardcoded to NL for now. This is a product decision and it means that
        NL dynamic pricing will be applied to stations that use this tariff profile
        even if they are not located in NL.

        This might be changed in the future making the area selectable.
        */}
        <EnergyMarketTariffProvider energyDeliveryArea="NL">
          <Stack align="flex-end" gap={4}>
            <MarketEnergyTariffsLabel currency={currency} />
            {costSetting.surchargePerKwh != 0 && (
              <div>
                {t('costSettingsCell.surchargePerKwh', 'Surcharge {{amount}}', {
                  amount: formatCurrency(costSetting.surchargePerKwh, currency),
                })}
              </div>
            )}
            {pricePerKwh != 0 && (
              <div>
                {t('costSettingsCell.pricePerKwh', 'Backup {{amount}}', {
                  amount: formatCurrency(pricePerKwh, currency),
                })}
              </div>
            )}
          </Stack>
        </EnergyMarketTariffProvider>
      </>
    );
  }
  return <span>{formatCurrency(pricePerKwh, currency)}</span>;
}
